import React, { useContext, useEffect, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { ClientDto } from 'api/Clients/Types/Client'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { PaymentMethodReplyDto } from 'api/PaymentProvider/Types/PaymentMethodReplyDto'
import { PaymentProviderFields } from 'api/PaymentProvider/Types/PaymentProviderFields'
import { DepositToWalletByPspTransaction } from 'api/Transaction/Types/DepositToWalletByPspTransaction'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { Spinner } from 'components/Spinner'
import { ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTransactionStatusAction } from 'store/transactions/transaction.action'
import { getIdFromLocationHeader } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { isEmpty } from 'utils/is-empty'

import { CampaignWithTradingAccount, DepositForm } from './DepositForm'

interface Props {
  client: ClientDto | null
  preSelectedWallet?: WalletDto

  setClient(): void
}

const DepositPage: React.FC<Props> = ({ client, preSelectedWallet, setClient }) => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [loading, setLoading] = useState(false)
  const [currency, setCurrency] = useState<
    null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  >(null)
  const [paymentProviderFields, setPaymentProviderFields] = useState<PaymentProviderFields[]>([])
  const [paymentProviders, setPaymentProviders] = useState<PaymentMethodReplyDto[]>([])
  const [clientTradingAccounts, setClientTradingAccounts] = useState<TradingAccountDto[]>([])

  const [wallet, setWalletId] = useState<string>('')
  const [paymentProviderId, setPaymentProviderId] = useState<string>('')

  const [confirmationData, setConfirmationData] = useState<DepositToWalletByPspTransaction>()
  const [isConfirmationModalOpen, setConfirmationModal] = useState<boolean>(false)

  const transactionState = useAppSelector((state) => state.transactions.status)
  const setToast = useContext(ToastContext)
  const dispatch = useAppDispatch()

  const { callback: fetchData, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!client) {
      return undefined
    }
    return walletsApiClient.getClientWallet(client.id)
  }, [client])

  useEffect(() => {
    // reset paymentProviderId on change client
    setPaymentProviderId('')
    setPaymentProviderFields([])
  }, [client, wallet])

  useEffect(() => {
    if (isEmpty(transactionState)) dispatch(fetchTransactionStatusAction(walletsApiClient))
  }, [transactionState, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

  const { data: wallets, isLoading } = useFetchOne(fetchData)

  const setWallet = async (walletId: string) => {
    const walletDto = (wallets || []).find((x) => x.id === walletId)
    if (walletDto && walletId) {
      setWalletId(walletDto.id)
      setCurrency(walletDto.currency.id)
      await getPaymentProvider(walletDto)
    } else {
      setPaymentProviders([])
    }
  }

  const getCampaignsFields = (): CampaignWithTradingAccount[] => {
    // from requirements - {campaigns.{firstElementOfArray}.name}
    return clientTradingAccounts.reduce<CampaignWithTradingAccount[]>((prev, curr) => {
      const dto: CampaignWithTradingAccount = {
        name: curr.campaigns[0].name,
        id: curr.campaigns[0].id,
        tradingAccountName: curr.name,
        tradingAccountId: curr.id,
      }
      return [...prev, dto]
    }, [] as CampaignWithTradingAccount[])
  }

  useEffect(() => {
    if (preSelectedWallet) {
      setWallet(preSelectedWallet.id)
    }
  }, [wallets, preSelectedWallet]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSubmit = async (values: DepositToWalletByPspTransaction) => {
    setConfirmationData(values)
    setConfirmationModal(true)
  }

  const handleConfirmationSubmit = async () => {
    if (confirmationData) {
      try {
        const response =
          await walletsApiClient.createDepositToWalletByPspTransaction(confirmationData)
        setToast({
          type: 'success',
          requireInteraction: true,
          title: 'Deposit has been submitted.',
          subtitle: 'Click for details',
          url: generatePath('/transactions/:id', {
            id: getIdFromLocationHeader(response.headers.location),
          }),
        })
        forceRefresh()
        setConfirmationModal(false)
      } catch (e) {
        console.error(e)
        setConfirmationModal(false)
      }
    }
  }

  const handleConfirmationCloseModal = () => {
    setConfirmationModal(false)
  }

  const getPaymentProvider = async (walletDto: WalletDto) => {
    if (client) {
      setLoading(true)
      const response = await walletsApiClient.getAvailablePaymentProviders('deposits', {
        walletId: walletDto.id,
        languageId: 'en',
        caller: 'bo',
      })
      setPaymentProviders(response.reply)
      setLoading(false)
    }
  }

  const getCampaignByWalletId = async (walletId: string) => {
    if (walletId) {
      setLoading(true)
      const response = await tradingAccountsApiClient.getCampaignsForDepositClient(walletId)
      setClientTradingAccounts(response.items)
      setLoading(false)
    }
  }

  const setWalletById = async (walletId: string) => {
    const walletDto = (wallets || []).find((x) => x.id === walletId)
    if (walletDto && walletId) {
      setWalletId(walletDto.id)
      setCurrency(walletDto.currency.id)
      await getPaymentProvider(walletDto)
    } else {
      setPaymentProviders([])
    }
  }

  const getPaymentProviderFields = async (providerId: string) => {
    if (client && providerId) {
      setLoading(true)
      const response = await walletsApiClient.getPaymentProviderField('deposits', {
        walletId: wallet,
        methodId: providerId,
        languageId: 'en',
        caller: 'bo',
      })
      setPaymentProviderFields(response.reply.form)
      setPaymentProviderId(providerId)
      setLoading(false)
    } else {
      setPaymentProviderFields([])
    }
  }

  const selectFromWalletName = () => {
    return paymentProviders?.find((x) => x.method_id === confirmationData?.fromPspId)?.method_name
  }

  const selectToWalletName = () => {
    return wallets?.find((x) => x.id === confirmationData?.toWalletId)?.name
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {isConfirmationModalOpen && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={handleConfirmationSubmit}
          onCancel={handleConfirmationCloseModal}
          visible={isConfirmationModalOpen}
          renderTitle={
            <div>
              <p>
                You are going to deposit funds to the wallet {selectToWalletName()}. The parameters
                selected are:
              </p>
              <div>
                <div id='field'>
                  <strong>Client:</strong> {client?.name}
                </div>
                <div id='field'>
                  <strong>From Payment Provider:</strong> {selectFromWalletName()}
                </div>
                <div id='field'>
                  <strong>To Wallet:</strong> {selectToWalletName()}
                </div>
                <div id='field'>
                  <strong>Amount:</strong> {confirmationData?.amount} {currency}
                </div>
              </div>
            </div>
          }
        />
      )}
      <DepositForm
        isLoading={loading}
        walletId={wallet}
        setWallet={setWalletById}
        getCampaignByWallet={getCampaignByWalletId}
        paymentProviderFields={paymentProviderFields}
        paymentProviders={paymentProviders}
        paymentProviderId={paymentProviderId}
        getPaymentProviderFields={getPaymentProviderFields}
        currency={currency}
        setCurrency={setCurrency}
        client={client}
        setClient={setClient}
        wallets={wallets || []}
        onSubmit={onSubmit}
        transactionState={transactionState}
        campaigns={getCampaignsFields()}
      />
    </React.Fragment>
  )
}

export default DepositPage
